import { ChatIcon, SettingsIcon } from '@/assets/icons'
import InviteModal from '@components/InviteModal'
import PageLoader from '@components/PageLoader'
import { Hero, TripOverview, TripOverviewSection } from '@components/TripOverview'
import { buildMemberTripOverviewData, sectionPath } from '@components/TripOverview/core'
import { PageColumn, PageScroll } from '@components/Page'
import { chatPath, unreadBadgeText } from '@components/Chat/core'
import { useChatUnread, useMe, useTrip, useTripJoinRequests, useTripOverview, useTripRoom } from '@hooks'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import EditTripModal from '../EditTripModal'
import { editTripDestinationsPath } from '../EditTripDestinationsFlow/paths'
import { ChatButton, HeroActions, TripSettingsButton, UnreadBadge } from './TripDetailPage.styles'
import { iconSizes } from '@/theme/tokens'
import { ErrorNote } from '@components/Notice'
import { isTripOwner } from '@lib/members'
import type { GetTripOverviewResponse } from '@orbit/api-types'
import type { Trip } from '@types'

/**
 * What the bento renders from before the overview request lands: trip meta off
 * the already-cached `trip` query, every section empty. Each tile is showing
 * its skeleton at that point, so only the hero's copy is on screen.
 */
const pendingOverview = (trip: Trip): GetTripOverviewResponse => ({
	trip: { name: trip.name, startDate: trip.startDate ?? null, dayCount: trip.dayCount },
	travelers: [],
	memberCount: 0,
	sections: {
		itinerary: { count: 0, events: [], travelSegments: [] },
		ideas: { count: 0, ideas: [] },
		accommodations: { count: 0, accommodations: [] },
		flights: { count: 0, firstFlight: null },
		files: { count: 0, files: [] },
		expenses: {
			count: 0,
			summary: { currencyCode: trip.defaultCurrencyCode, groupTotal: 0 },
			payUps: [],
		},
	},
})

/**
 * The trip home. Every tile comes from the one aggregated overview request —
 * the per-section pages fetch their own full lists. `trip` and `me` stay their
 * own (already cached) queries: the modals need the trip's code, owner and
 * currency, none of which the bento renders.
 */
const TripDetailPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: me } = useMe()
	const { data: overview, isLoading: overviewLoading } = useTripOverview(tripId)
	const { data: unread } = useChatUnread(tripId)
	const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false)
	const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
	// The trip's room keeps the Chat badge live while the trip home is open.
	useTripRoom(tripId)
	// The owner's waiting Join requests, as a chip beside the avatars; nobody else fetches them.
	const isOwner = isTripOwner(me, trip)
	const { data: joinRequests } = useTripJoinRequests(tripId, isOwner === true)

	if (tripLoading) {
		return (
			<PageScroll>
				<PageLoader label="Loading trip details…" />
			</PageScroll>
		)
	}

	if (!trip) {
		return (
			<PageScroll>
				<PageColumn>
					<ErrorNote>Trip not found</ErrorNote>
				</PageColumn>
			</PageScroll>
		)
	}

	const overviewData = buildMemberTripOverviewData(overview ?? pendingOverview(trip))
	const unreadBadge = unreadBadgeText(unread?.unreadCount ?? 0)

	// One query feeds every tile, so they skeleton together.
	const tileLoading = {
		itinerary: overviewLoading,
		ideas: overviewLoading,
		accommodations: overviewLoading,
		flights: overviewLoading,
		files: overviewLoading,
		money: overviewLoading,
	}

	const handleNavigate = (section: TripOverviewSection) => {
		navigate(sectionPath(tripId ?? '', section))
	}

	// The destinations editor comes back here, not to the itinerary, when
	// opened from the overview.
	const openDestinationsEditor = () => {
		if (!tripId) return
		navigate(editTripDestinationsPath(tripId), { state: { returnTo: `/trips/${tripId}` } })
	}

	return (
		<PageScroll>
			<PageColumn>
				<Hero
					hero={overviewData.hero}
					onAvatarsClick={() => navigate(`/trips/${tripId}/members`)}
					onInviteClick={() => setIsInviteModalOpen(true)}
					pendingRequestCount={joinRequests?.requests.length ?? 0}
					onRequestsClick={() => navigate(`/trips/${tripId}/members`)}
					action={
						<HeroActions>
							<ChatButton
								onClick={() => navigate(chatPath(tripId ?? ''))}
								aria-label={unreadBadge ? `Chat, ${unread?.unreadCount} unread` : 'Chat'}
							>
								<ChatIcon sx={{ fontSize: iconSizes.md }} />
								<span>Chat</span>
								{unreadBadge && <UnreadBadge aria-hidden>{unreadBadge}</UnreadBadge>}
							</ChatButton>
							<TripSettingsButton onClick={() => setIsEditTripModalOpen(true)} aria-label="Trip settings">
								<SettingsIcon sx={{ fontSize: iconSizes.md }} />
								<span>Trip Settings</span>
							</TripSettingsButton>
						</HeroActions>
					}
				/>

				<TripOverview
					data={overviewData}
					mode="member"
					loading={tileLoading}
					onNavigate={handleNavigate}
					onSetTripDates={openDestinationsEditor}
				/>
			</PageColumn>

			<EditTripModal
				isOpen={isEditTripModalOpen}
				onClose={() => setIsEditTripModalOpen(false)}
				tripId={tripId || ''}
				tripCode={trip.code}
				initialName={trip.name}
				initialCurrencyCode={trip.defaultCurrencyCode}
				joinApprovalRequired={trip.joinApprovalRequired}
				isOwner={isOwner}
			/>

			<InviteModal
				isOpen={isInviteModalOpen}
				onClose={() => setIsInviteModalOpen(false)}
				tripCode={trip.code}
			/>
		</PageScroll>
	)
}

export default TripDetailPage
